/// <reference types="vite-plugin-svgr/client" />

import {MouseEvent, useEffect, useState} from "react";
import {
  Poll,
  defaultTheme,
  defaultImage,
  getActivePolls,
} from "@/hooks/useGetUserPolls";
import {sanitize} from "@/utils/sanitize";

import Lock from "@/icons/lock.svg?react";
import Pig from "@/icons/pig.svg?react";
import PlayArrow from "@/icons/play-arrow.svg?react";
import Rabbit from "@/icons/rabbit.svg?react";
import locales from "@/locales/page-pause.json";
import {getUserClaims, UserClaims} from "@/features/api/lib/firebase";
import FeedbackDialog from "@/components/FeedbackDialog";
import ReactivatedUserMessage from "@/features/feed/components/ReactivatedUserMessage";
import PhoneVerificationIssueMessage from "@/features/feed/components/PhoneVerificationIssueMessage";
import {REACTIVATED_USER_TIMESTAMP_KEY} from "@/utils/localStorage";

const locale = locales.cs;

/**
 * Persist the dismissal of the reactivation welcome message by dropping the
 * timestamp that gates it, so the message stays hidden after a reload or a
 * navigation back to the feed. A later reactivation writes the key again and
 * the message is shown for that new cycle.
 *
 * Defined outside the component so the callback identity stays stable for the
 * memoized `Message`. Errors are swallowed on purpose: React error boundaries
 * do not catch throws from event handlers and localStorage can be unavailable
 * (private browsing), where failing to dismiss must not break the feed.
 */
const getReactivatedUserTimestamp = () => {
  try {
    return Number(window.localStorage.getItem(REACTIVATED_USER_TIMESTAMP_KEY));
  } catch {
    // Storage unavailable (private browsing): behave as if never reactivated
    // rather than letting the read throw during render and break the feed.
    return 0;
  }
};

const dismissReactivatedUserMessage = () => {
  try {
    window.localStorage.removeItem(REACTIVATED_USER_TIMESTAMP_KEY);
  } catch (error) {
    console.error(
      `Nepodařilo se odstranit "${REACTIVATED_USER_TIMESTAMP_KEY}" z localStorage:`,
      error,
    );
  }
};

// classes for each color must be defined for Tailwind JIT to work
// https://tailwindcss.com/docs/content-configuration#dynamic-class-names
const colorClasses = {
  blue: "bg-feed-blue",
  green: "bg-feed-green",
  gray: "bg-feed-gray",
  orange: "bg-feed-orange",
  purple: "bg-feed-purple",
  red: "bg-feed-red",
  yellow: "bg-feed-yellow",
};

const tileContentClasses = {
  blue: "bg-feed-blue-bg/80 text-feed-on-blue fill-feed-on-blue",
  green: "bg-feed-green-bg/80 text-feed-on-green fill-feed-on-green",
  gray: "bg-feed-gray-bg/80 text-feed-on-gray fill-feed-on-gray",
  orange: "bg-feed-orange-bg/80 text-feed-on-orange fill-feed-on-orange",
  purple: "bg-feed-purple-bg/80 text-feed-on-purple fill-feed-on-purple",
  red: "bg-feed-red-bg/80 text-feed-on-red fill-feed-on-red",
  yellow: "bg-feed-yellow-bg/80 text-feed-on-yellow fill-feed-on-yellow",
};

const computePolls = (polls: Poll[]) => {
  let sortedPolls: Poll[] = [];

  // Filter out hidden, completed polls and polls with deadline in the past.
  sortedPolls = getActivePolls(polls);

  // Sort polls by priority and deadline.
  // 100 is the highest priority. Poll with the highest priority will be shown first.
  // If two polls have the same priority, the poll with the nearest deadline will be shown first.
  sortedPolls = sortedPolls.sort((a: Poll, b: Poll) => {
    if (a.priority === b.priority) {
      return (b.deadline ?? 0) - (a.deadline ?? 0);
    }
    return (b.priority ?? 0) - (a.priority ?? 0);
  });

  return sortedPolls;
};

export interface FeedProps {
  /**
   * Polls to display in the feed component
   */
  polls: Poll[];

  /**
   * Navigate to the poll page or open the external link in a new tab
   */
  navigateToPoll: (poll: Poll) => void;
}

export const Feed = ({polls = [], navigateToPoll}: FeedProps) => {
  const sortedPolls = computePolls(polls);
  const [userClaims, setUserClaims] = useState<UserClaims | null>(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  useEffect(() => {
    getUserClaims().then((userClaims) => {
      setUserClaims(userClaims);
    });
  }, []);

  const onTileClick = (event: MouseEvent<HTMLButtonElement>, poll: Poll) => {
    // show a message if the poll is locked and do not navigate
    if (poll.locked) {
      const tileMessage = event.currentTarget.querySelector(
        "#tileMessage",
      ) as HTMLDivElement;

      if (!tileMessage) return;

      tileMessage.classList.add("visible", "translate-y-0");

      setTimeout(() => {
        tileMessage.classList.remove("visible", "translate-y-0");
      }, 3000);

      return;
    }

    navigateToPoll(poll);
  };

  const renderMessage = (wrapperClassName: string) => {
    const reactivatedUserTimestamp = getReactivatedUserTimestamp();

    // render the message only if the user was reactivated less than 2 hours ago
    const canRenderMessageForReactivatedUser =
      reactivatedUserTimestamp &&
      reactivatedUserTimestamp + 2 * 60 * 60 * 1000 > Date.now();

    return (
      <>
        {/* Reactivation welcome message is hidden when the account is at risk
            from a failed phone verification — that warning takes priority. */}
        {userClaims?.isPendingDeletion &&
          !userClaims?.isPhoneVerificationIssue &&
          canRenderMessageForReactivatedUser && (
            <div className={wrapperClassName}>
              <ReactivatedUserMessage
                email={userClaims?.email}
                onClose={dismissReactivatedUserMessage}
              />
            </div>
          )}
        {/* "Account at risk" warning for users in pending_deletion because we
            could not verify their phone number. Gated on isPendingDeletion as
            well so a stale phoneVerificationIssue flag cannot keep showing a
            deletion warning to accounts that are no longer at risk. */}
        {userClaims?.isPendingDeletion &&
          userClaims?.isPhoneVerificationIssue && (
            <div className={wrapperClassName}>
              <PhoneVerificationIssueMessage
                onContactSupport={() => setIsFeedbackOpen(true)}
              />
            </div>
          )}
      </>
    );
  };

  const renderPause = () => {
    return (
      <div // container with background image
        className="p-g-1 size-full bg-[url('/images/background-pig.jpg')] bg-cover bg-[center_top] bg-no-repeat"
      >
        <div // text container with transparent background color
          className="text-on-surface bg-surface/70 p-g-2 relative flex size-full flex-col items-center justify-center rounded-md font-medium"
        >
          {renderMessage("absolute top-0 w-full")}

          <h1 className="mb-4 text-4xl font-black sm:mb-16 sm:text-5xl">
            {locale.pause}
          </h1>
          <p className="mb-g-1 sm:mb-g-2 text-center text-2xl sm:text-3xl">
            {locale.subtitle}
          </p>
          <p className="text-md readable max-w-prose text-center sm:text-xl">
            {sanitize(locale.description)}
          </p>
        </div>
      </div>
    );
  };

  const feedbackDialog = (
    <FeedbackDialog
      isOpen={isFeedbackOpen}
      onClose={() => setIsFeedbackOpen(false)}
    />
  );

  if (sortedPolls.length === 0)
    return (
      <>
        {renderPause()}
        {feedbackDialog}
      </>
    );

  return (
    <>
      {renderMessage("mx-g-2 mt-g-2 sm:mx-0")}

      <div // grid
        className={[
          "gap-g-1 p-g-1 xs:gap-g-2 xs:p-g-2 max-w-b-xl mx-auto grid w-full grid-cols-1",
          "sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6",
        ].join(" ")}
      >
        {sortedPolls.map((poll) => (
          <button // tile
            className={[
              "group h-56 cursor-pointer overflow-hidden rounded-t-md",
              "lg:first:col-span-2 lg:first:row-span-2 lg:first:h-[29rem]",
              "xl:nth-[4]:col-span-2 xl:nth-[5]:col-span-2 xl:nth-[5]:row-span-2 xl:nth-[5]:h-[29rem]",
              "transition-transform ease-in-out hover:scale-[103%] hover:transform",
            ].join(" ")}
            key={poll.slug}
            data-testid={poll.slug}
            onClick={(e) => onTileClick(e, poll)}
          >
            <div // tile content
              className={`relative size-full rounded-md ${tileContentClasses[poll.theme || defaultTheme]}`}
            >
              <div // tile image
                className="bottom-ch-2 inset-x-g-2 top-g-2 absolute"
              >
                <img
                  className="h-full w-full object-contain"
                  src={`/images/feed/${poll.image || defaultImage}`}
                />
              </div>

              <div // tile label
                className={[
                  "h-ch-2 px-g-2 absolute bottom-0 flex w-full flex-row",
                  "items-center rounded-b-md text-left text-sm leading-4",
                  colorClasses[poll.theme || defaultTheme],
                ].join(" ")}
              >
                {poll.title || "None"}
                <span className="grow"></span>
                {poll.locked ? (
                  <Lock className="size-i-2 min-w-i-2 ml-g-1" />
                ) : (
                  <PlayArrow className="size-i-2 min-w-i-2 ml-g-1" />
                )}
              </div>
              <div // tile credits
                className="right-g-1 top-g-1 absolute flex items-center text-sm"
              >
                {poll.credits ? (
                  <>
                    <span>{poll.credits}&nbsp;</span>
                    <Pig className="size-i-1" />
                  </>
                ) : (
                  <Rabbit className="size-i-1" />
                )}
              </div>
              {poll.locked && (
                <div // tile message for locked poll
                  className={[
                    "bottom-ch-2 p-g-2 invisible absolute inset-0 flex flex-col",
                    "items-center justify-center rounded-t-md border-b border-solid border-inherit text-center",
                    "-translate-y-full transition-transform duration-300 ease-in-out",
                    "group-hover:visible group-hover:translate-y-0",
                    colorClasses[poll.theme || defaultTheme],
                  ].join(" ")}
                  id="tileMessage"
                >
                  <div>{poll.lockedMessage}</div>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      {feedbackDialog}
    </>
  );
};
